import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Route, Loader2, Navigation, CircleCheck, Circle, LocateFixed, Crosshair } from 'lucide-react';
import { currentPosition, orderStops, routeLegs } from '../lib/route';

const km = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);

import RouteObjects from './RouteObjects';
import LoggerRoutes from './LoggerRoutes';

export default function RoutePlanner({ t, project, center, onToggleDone, onClose }) {
  const [start, setStart] = useState(null);
  useEffect(() => { currentPosition({ lat: center.lat, lon: center.lng }).then(setStart); }, [center.lat, center.lng]);
  const open = project.reports.filter((r) => !r.done);
  const stops = start ? orderStops(start, open) : [];
  const legs = start ? routeLegs(start, stops) : [];
  const total = stops.reduce((s, r) => s + r.dist, 0);
  const done = project.reports.filter((r) => r.done);

  return createPortal(
    <div className="editor-backdrop" data-testid="route-planner-dialog" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="editor hist">
        <div className="editor-head">
          <h4><Route size={18} /> {t.routeDayTitle}</h4>
          <button className="icon-btn" data-testid="route-planner-close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        {!start ? <p className="pay-wait" data-testid="route-planner-locating"><Loader2 size={16} className="spin" /> {t.routeLocating}</p> : (
          <>
            <p className="muted small-hint" data-testid="route-planner-start">{start.gps ? <LocateFixed size={13} /> : <Crosshair size={13} />} {start.gps ? t.routeFromGps : t.routeFromMap}</p>
            {!open.length ? <p data-testid="route-planner-empty">{t.routeNone}</p> : (
              <>
                <div className="hist-kpis">
                  <span data-testid="route-planner-count"><b>{open.length}</b> {t.routeOpen}</span>
                  <span data-testid="route-planner-distance"><b>{km(total)}</b> {t.routeAir}</span>
                </div>
                {legs.map((leg, li) => (
                  <div key={li} className="route-leg" data-testid={`route-leg-${li}`}>
                    {legs.length > 1 && <b>{t.routeLeg} {li + 1} / {legs.length}</b>}
                    <ol start={li * 10 + 1}>
                      {leg.stops.map((r) => (
                        <li key={r.id}>
                          <span>{r.title}{r.address ? <small>{r.address}</small> : null}</span>
                          <em>{km(r.dist)}</em>
                          <button className="icon-btn" data-testid={`route-done-${r.id}`} title={t.repDone} onClick={() => onToggleDone(r.id, true)}><Circle size={16} /></button>
                        </li>
                      ))}
                    </ol>
                    <a className="btn btn-green" data-testid={`route-open-leg-${li}`} href={leg.url} target="_blank" rel="noreferrer"><Navigation size={16} /> {legs.length > 1 ? `${t.routeOpenLeg} ${li + 1}` : t.routeOpenMaps}</a>
                  </div>
                ))}
              </>
            )}
            <RouteObjects t={t} project={project} start={start} />
            <LoggerRoutes t={t} project={project} start={start} />
            {done.length > 0 && (
              <div className="route-done" data-testid="route-planner-done">
                <b>{t.repDone} ({done.length})</b>
                {done.map((r) => <button key={r.id} className="addr-chip" data-testid={`route-undo-${r.id}`} onClick={() => onToggleDone(r.id, false)}><CircleCheck size={12} /> {r.title}</button>)}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
